import { ArrowLeftOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Alert, Button, Card, Empty, List, Pagination, Row, Space, Spin, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { Loader } from 'components/common/Loader';
import { ApplicationCard } from 'components/molecules/ApplicationCard';
import { BusinessList } from 'components/organisms/BusinessList';
import { useTheme } from 'context/ThemeContext';
import { useAuthentication } from 'hooks/useAuthentication';
import Cookies from 'js-cookie';
import { AcuityContext } from 'models/Application';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetApplicationsByDivisionQuery } from 'redux/services/rudyCadabby/rudyCadabbyApi';
import { setNavSearchValue } from 'redux/slices/businessListSlice';
import { useAppSelector } from 'redux/store';
import { useRenderPerformanceMonitor } from 'utils/performanceUtils';

// Constants for pagination
const PAGE_SIZE = 12;
// Increase the interval to reduce CPU usage
const COOKIE_CHECK_INTERVAL = 1000; // Check every second instead of 200ms

// Create a stable debounce function that cleans up properly
const createDebouncedCheck = (wait: number, callback: Function) => {
  let timeout: NodeJS.Timeout | null = null;

  return {
    check: (...args: any[]) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        callback(...args);
      }, wait);
    },
    cleanup: () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    }
  };
};

/**
 * Helper function to get customer context from cookies
 * @returns The selected customer from cookies or undefined
 */

// const cookieOptions = process.env.REACT_APP_COOKIE_PREFIX === 'localdev' ? { expires: 365 } : { expires: 365, domain: 'mdsiinc.com' };

const getCustomerFromCookie = (): AcuityContext['selectedCustomer'] | undefined => {
  const customerCookie = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`);

  if (customerCookie) {
    try {
      const { selectedCustomer } = JSON.parse(customerCookie) as AcuityContext;
      return selectedCustomer;
    } catch (error) {
      console.error('Error parsing customer cookie:', error);
      return undefined;
    }
  }
  return undefined;
};

export const DivisionApplicationsPage = memo((): JSX.Element => {
  // Monitor render performance
  useRenderPerformanceMonitor('DivisionApplicationsPage');
  const { navSearchValue: searchValue } = useAppSelector((state) => state.businessList);
  const { permissions } = useAuthentication();
  const { isDarkMode } = useTheme();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const { xs } = useBreakpoint();
  const isMobile = xs;
  const lastCookieValue = useRef<string | null>(null);

  // State to track if customer is selected and force re-renders
  const [customerState, setCustomerState] = useState<AcuityContext['selectedCustomer'] | undefined>(undefined);
  // Add initial loading state to prevent flashing BusinessList
  const [initialLoading, setInitialLoading] = useState(true);

  // Track if we're in a loading state due to customer selection
  const [isCustomerSelectionLoading, setIsCustomerSelectionLoading] = useState(false);

  // Use useRef to track if component is mounted
  const isMounted = useRef(true);

  // Reference to store current selection interval
  const currentSelectionInterval = useRef<NodeJS.Timeout | null>(null);

  // Reference to store the debounced checker
  const debouncedChecker = useRef<ReturnType<typeof createDebouncedCheck> | null>(null);

  // Cleanup function for intervals and timeouts
  const cleanup = useCallback(() => {
    if (currentSelectionInterval.current) {
      clearInterval(currentSelectionInterval.current);
      currentSelectionInterval.current = null;
    }
    if (debouncedChecker.current) {
      debouncedChecker.current.cleanup();
    }
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Get customer from cookie and update state on initial load
  useEffect(() => {
    const customerFromCookie = getCustomerFromCookie();
    if (customerFromCookie) {
      setCustomerState(customerFromCookie);
      // Store the current cookie value for comparison
      lastCookieValue.current = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`) || null;
    }
    // Set initialLoading to false after checking for cookie
    setInitialLoading(false);
  }, []);

  // Listen for location changes which happen after customer selection
  useEffect(() => {
    const customerFromCookie = getCustomerFromCookie();
    if (customerFromCookie) {
      setCustomerState(customerFromCookie);
      setIsCustomerSelectionLoading(false);
    }
  }, [location.pathname]);

  // Effect for cookie checking
  useEffect(() => {
    if (!customerState) {
      // Create a new debounced checker if needed
      if (!debouncedChecker.current) {
        debouncedChecker.current = createDebouncedCheck(250, () => {
          if (!isMounted.current) return;

          const currentCookieValue = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`) || null;
          if (currentCookieValue !== lastCookieValue.current) {
            lastCookieValue.current = currentCookieValue;
            const customerFromCookie = getCustomerFromCookie();
            if (customerFromCookie) {
              setCustomerState(customerFromCookie);
              setIsCustomerSelectionLoading(false);
            }
          }
        });
      }

      const intervalId = setInterval(() => {
        debouncedChecker.current?.check();
      }, COOKIE_CHECK_INTERVAL);

      return () => {
        clearInterval(intervalId);
        cleanup();
      };
    }

    return cleanup;
  }, [customerState, cleanup]);

  const handleSelectNewCustomer = useCallback(() => {
    cleanup(); // Clean up any existing intervals
    const cookieOptions = process.env.REACT_APP_COOKIE_PREFIX === 'localdev' ? { expires: 365 } : { expires: 365, domain: 'mdsiinc.com' };

    Cookies.remove(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`, cookieOptions);
    setCustomerState(undefined);
    lastCookieValue.current = null;
    dispatch(setNavSearchValue(''));
  }, [cleanup, dispatch]);

  // Modify the customer selection handler to be more efficient
  const handleCustomerSelected = useCallback(() => {
    cleanup(); // Clean up any existing intervals
    setIsCustomerSelectionLoading(true);

    let attempts = 0;
    const maxAttempts = 20; // 20 * 100ms = 2 seconds total

    const checkForCookie = () => {
      if (!isMounted.current) return false;

      const customerFromCookie = getCustomerFromCookie();
      if (customerFromCookie) {
        setCustomerState(customerFromCookie);
        setIsCustomerSelectionLoading(false);
        return true;
      }
      return false;
    };

    currentSelectionInterval.current = setInterval(() => {
      attempts++;
      if (checkForCookie() || attempts >= maxAttempts) {
        cleanup();
        if (attempts >= maxAttempts) {
          setIsCustomerSelectionLoading(false);
        }
      }
    }, 100);

    return cleanup;
  }, [cleanup]);

  // Fetch applications data only if customer is selected
  const { data, isLoading, isFetching, isError, error } = useGetApplicationsByDivisionQuery(customerState?.id ?? skipToken, {
    // Skip if we're in the customer selection loading state
    skip: !customerState?.id
  });

  // Create a memoized permission check function
  const hasPermission = useCallback(
    (permissionName: string) => {
      return permissions?.includes(permissionName) ?? false;
    },
    [permissions]
  );

  // Create a memoized permission map for faster lookups
  const permissionMap = useMemo(() => {
    if (!permissions) return new Set();
    return new Set(permissions);
  }, [permissions]);

  // Optimize permission filtering with Set lookup
  const permissionsFilteredApplications = useMemo(() => {
    if (!data?.data) return [];

    // Early return if no permissions
    if (!permissionMap.size) return [];

    return data.data.filter((app) => permissionMap.has((app as any).application.accessPermissionName));
  }, [data?.data, permissionMap]);

  // Memoize filtered applications to prevent recalculation on each render
  const filteredApplications = useMemo(() => {
    // Early return if no data
    if (!permissionsFilteredApplications.length) return [];

    // Filter applications based on mobile optimization and menu tab
    let filtered = permissionsFilteredApplications.filter((app) => app.application.menuTab === 'Applications' && (!xs || app.application.isMobileOptimized));

    // Apply search filter if search value exists
    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter((app) => app.application.name.toLowerCase().includes(searchLower) || app.application.description.toLowerCase().includes(searchLower));
    }

    // Sort applications alphabetically by name
    return filtered.sort((a, b) => a.application.name.localeCompare(b.application.name));
  }, [permissionsFilteredApplications, xs, searchValue]);

  // Paginate the data with proper memoization
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredApplications.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredApplications, currentPage, PAGE_SIZE]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top of the list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // If in initial loading state, show a loading spinner
  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin indicator={<Loader isDarkMode={isDarkMode} transparent componentLoader />} />
      </div>
    );
  }

  // If no customer is selected, render the BusinessList component
  if (!customerState) {
    return (
      <div className="select-customer-container" style={{ maxWidth: 1920 }}>
        <BusinessList onCustomerSelect={handleCustomerSelected} />
      </div>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <Alert
        type="error"
        message="Error Loading Applications"
        description={
          <Space direction="vertical">
            <Typography.Text>There was an error loading the applications. Please try refreshing the page.</Typography.Text>
            <Typography.Text type="secondary">Error details: {(error as any)?.data?.message || 'Unknown error'}</Typography.Text>
          </Space>
        }
      />
    );
  }

  // Combined loading state
  const isLoadingState = isLoading || isFetching || isCustomerSelectionLoading;

  return (
    <div
      className="warehouse-applications-wrapper"
      style={{
        maxWidth: 1920,
        height: '100%',
        padding: isMobile ? '0' : '0'
      }}>
      <Card
        className="division-applications-container"
        bordered={false}
        style={{
          borderRadius: isMobile ? 0 : 8,
          padding: isMobile ? '0px' : '0px 0px',
          boxShadow: isMobile ? 'none' : '0 2px 8px rgba(0,0,0,0.05)',
          background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgb(229, 232, 238)',
          opacity: isDarkMode ? 0.8 : 1,
          border: isDarkMode ? '1px solid rgb(69, 68, 76)' : '1px solid #e8e8e8',
          width: '100%',
          overflow: 'hidden',
          margin: isMobile ? 0 : undefined
        }}>
        <Row
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
          style={{
            marginLeft: 0,
            marginRight: 0,
            marginBottom: isMobile ? 16 : 24,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center'
          }}>
          <Typography.Title
            level={4}
            style={{
              margin: 0,
              fontWeight: 500,
              color: isDarkMode ? '#fff' : '#000',
              fontSize: isMobile ? '16px' : undefined,
              marginBottom: isMobile ? 12 : 0
            }}>
            {customerState?.business?.name ? `${customerState.business.name} - ${customerState.name}` : 'Division Applications'}
          </Typography.Title>
          {!isMobile && (
            <Button icon={<ArrowLeftOutlined />} type="primary" onClick={handleSelectNewCustomer} style={{ width: isMobile ? '100%' : 'auto' }}>
              Select New Customer
            </Button>
          )}
        </Row>

        {isLoadingState ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spin indicator={<Loader isDarkMode={isDarkMode} transparent componentLoader loadingText="Loading applications..." />} />
          </div>
        ) : (
          <>
            {!paginatedApplications.length ? (
              <Empty
                description={
                  <Typography.Text type="secondary" style={{ fontSize: isMobile ? 14 : 16, color: isDarkMode ? '#fff' : '#000' }}>
                    {searchValue ? 'No applications match your search' : 'No applications available for this division'}
                  </Typography.Text>
                }
                style={{
                  padding: '40px 0',
                  background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : '#f5f5f5',
                  borderRadius: 8,
                  width: '100%'
                }}
              />
            ) : (
              <div style={{ position: 'relative', width: '100%' }}>
                <List
                  grid={{
                    column: 1,
                    gutter: isMobile ? 16 : 24,
                    xs: 1,
                    sm: 1,
                    md: 2,
                    lg: 3,
                    xl: 3,
                    xxl: 3
                  }}
                  dataSource={paginatedApplications}
                  renderItem={(item): JSX.Element => {
                    // Create a properly typed application object for the ApplicationCard
                    return (
                      <List.Item
                        style={{
                          marginBottom: isMobile ? 16 : 24,
                          height: '100%',
                          width: '100%'
                          // padding: isMobile ? '0 4px' : '0 12px'
                        }}>
                        <ApplicationCard
                          key={item.id}
                          application={{
                            // Base Application properties
                            id: item.id,
                            name: item.application.name,
                            description: item.application.description,
                            url: item.application.url,
                            // These properties are required by the Application interface
                            menuTab: item.application.menuTab,
                            isMobileOptimized: item.application.isMobileOptimized,
                            // This property might not exist on the original object, provide a default
                            accessPermissionName: (item as any).application.accessPermissionName || '',
                            // ExtendedApplication properties
                            logoUrl: item.application.logoUrl,
                            // Include the original application object with required accessPermissionName
                            application: {
                              ...item.application,
                              // Add the missing accessPermissionName property
                              accessPermissionName: (item as any).application.accessPermissionName || ''
                            },
                            // Include the division data if present
                            division: item.division
                          }}
                        />
                      </List.Item>
                    );
                  }}
                  style={{
                    marginTop: isMobile ? 4 : 8,
                    background: 'transparent',
                    borderRadius: 8,
                    width: '100%'
                  }}
                />
              </div>
            )}
          </>
        )}

        {!isLoadingState && filteredApplications.length > PAGE_SIZE && (
          <Pagination
            current={currentPage}
            onChange={handlePageChange}
            total={filteredApplications.length}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            size={isMobile ? 'small' : 'default'}
            style={{
              marginTop: isMobile ? 16 : 24,
              textAlign: 'center',
              padding: isMobile ? '4px 0' : '8px 0',
              width: '100%'
            }}
            responsive
          />
        )}
      </Card>
    </div>
  );
});
